import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { getEntityEndpoint } from '@/shared/api/openmetadata';
import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import { assetKeys } from '@/shared/lib/query/queryKeys';
import type { UpdateOwnerItem } from '@/shared/types/owner';

import type { TeamOwner, UserOwner } from '@/entities/owner';
import { buildNewOwners, buildOwnerOperations, patchOwners } from '@/entities/owner';

export const useUpdateAssetOwners = (entityType: string, assetId: string, fqn: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      selectedTeamId,
      selectedUserIds,
      teams,
      users,
      currentOwners,
    }: {
      selectedTeamId: string | null;
      selectedUserIds: string[];
      teams: TeamOwner[];
      users: UserOwner[];
      currentOwners: UpdateOwnerItem[];
    }) => {
      const newOwners = buildNewOwners({
        selectedTeamId,
        selectedUserIds,
        teams,
        users,
      });
      const operations = buildOwnerOperations({
        newOwners,
        currentOwners,
      });

      const apiPath = getEntityEndpoint(entityType);
      await patchOwners({
        resourceId: assetId,
        apiPath,
        operations,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.assetBaseInfo(entityType, fqn) });
      notification.success({
        message: NOTIFICATION_MESSAGES.OWNER_UPDATE_SUCCESS,
        description: NOTIFICATION_MESSAGES.OWNER_UPDATE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
    onError: (error: unknown) => {
      queryClient.invalidateQueries({ queryKey: assetKeys.assetBaseInfo(entityType, fqn) });
      notification.error({
        message: NOTIFICATION_MESSAGES.OWNER_UPDATE_ERROR,
        description: error instanceof Error ? error.message : NOTIFICATION_MESSAGES.OWNER_UPDATE_ERROR_DESC,
        placement: 'bottomLeft',
        duration: 5,
      });
    },
  });
};
